import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getIdentityVerificationStatus,
  startIdentityVerificationSession,
  type KycActionErrorCode,
  type KycStatusData,
} from '@/lib/api';
import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';

/**
 * Post-submit KYC self-service hook (PR-DIDIT-6). Loads the normalized status
 * once on mount, polls LIGHTLY only while a session is in flight, and exposes
 * `start` (start/resume) + `refresh` (manual "Aggiorna stato"). No PII / token
 * ever leaves the request body; nothing is logged.
 */

const POLL_INTERVAL_MS = 8000;
const IN_FLIGHT: ReadonlySet<KycStatusData['status']> = new Set([
  'SESSION_CREATED',
  'IN_PROGRESS',
]);

export interface UseIdentityVerificationStatus {
  phase: 'loading' | 'ready' | 'error';
  data: KycStatusData | null;
  actionError: KycActionErrorCode | null;
  starting: boolean;
  refresh: () => Promise<void>;
  start: () => Promise<void>;
}

export function useIdentityVerificationStatus(
  uploadToken: string,
  options: { onSessionExpired: () => void },
): UseIdentityVerificationStatus {
  const { onSessionExpired } = options;
  const wizardSessionId = useWizardSessionIdContext();
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [data, setData] = useState<KycStatusData | null>(null);
  const [actionError, setActionError] = useState<KycActionErrorCode | null>(null);
  const [starting, setStarting] = useState(false);

  // Guard against setState after unmount (redirect navigation / polling).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Keep the latest `onSessionExpired` in a ref so `refresh`/`start` stay stable
  // even when the caller passes a fresh closure each render — otherwise the
  // load effect would re-fire on every render (fetch loop).
  const onExpiredRef = useRef(onSessionExpired);
  useEffect(() => {
    onExpiredRef.current = onSessionExpired;
  }, [onSessionExpired]);

  const refresh = useCallback(async (): Promise<void> => {
    const res = await getIdentityVerificationStatus(uploadToken);
    if (!mountedRef.current) return;
    if (res.ok) {
      setData(res.data);
      setActionError(null);
      setPhase('ready');
      return;
    }
    if (res.code === 'UPLOAD_TOKEN_INVALID') {
      onExpiredRef.current();
      return;
    }
    setPhase('error');
    setActionError(res.code);
  }, [uploadToken]);

  // Initial load.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Lightweight polling — only while a session is in flight; cleared on
  // terminal state / unmount.
  useEffect(() => {
    if (!data || !IN_FLIGHT.has(data.status)) return;
    const id = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [data, refresh]);

  const start = useCallback(async (): Promise<void> => {
    setStarting(true);
    setActionError(null);
    const res = await startIdentityVerificationSession(
      uploadToken,
      wizardSessionId || undefined,
    );
    if (!mountedRef.current) return;
    if (res.ok) {
      if (res.data.redirectUrl) {
        // Top-level navigation (NOT a popup) → not blocked by popup blockers.
        // On return, the DocumentsPage sessionStorage recovery restores the
        // token and the card re-fetches the (now advanced) status.
        window.location.assign(res.data.redirectUrl);
        return;
      }
      // No redirect (already terminal / not required) → reflect the new state.
      setStarting(false);
      await refresh();
      return;
    }
    setStarting(false);
    if (res.code === 'UPLOAD_TOKEN_INVALID') {
      onExpiredRef.current();
      return;
    }
    setActionError(res.code);
  }, [uploadToken, wizardSessionId, refresh]);

  return { phase, data, actionError, starting, refresh, start };
}
